import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CeuUnitService } from '@service/ceu-unit.service';
import { ExternalCommunicationService } from '@service/external-communication.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { VesselService } from '@service/vessel.service';
import { ceuMaxNum, CeuUnit } from 'icreate/common/models/ceu-unit';
import { ExternalCommunication } from 'icreate/common/models/external-communication';
import { FcuExternalCommunications } from 'icreate/common/models/fcu-unit';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { CeuUnitSocketService } from '@service/ceu-unit-socket.service';
import { CeuUnitDialogComponent } from '@components/ceu-unit-dialog/ceu-unit-dialog.component';
import { CommunicationProtocol } from 'icreate/common/models/communication-protocol';
import { CommunicationProtocolService } from '@service/communication-protocol.service';

@Component({
    selector: 'app-external-comm-settings',
    templateUrl: './external-comm-settings.component.html',
    styleUrl: './external-comm-settings.component.css',
    standalone: false,
})
export class ExternalCommSettingsComponent implements OnInit, OnDestroy {
    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean = true;

    isLoading: boolean = false;

    commsList: FcuExternalCommunications[] = [];
    initialCommsList: FcuExternalCommunications[] = [];

    communicationProtocols: CommunicationProtocol[];

    private destroy$ = new Subject<void>();

    constructor(
        private vesselService: VesselService,
        private router: Router,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
        private externalCommunicationService: ExternalCommunicationService,
        private ceuUnitService: CeuUnitService,
        private messageDialogService: MessageDialogService,
        private ceuService: CeuUnitService,
        private ceuSocketService: CeuUnitSocketService,
        private communicationProtocolService: CommunicationProtocolService
    ) {}

    async ngOnInit(): Promise<void> {
        this.ceuSocketService
            .unitCreated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((ceuUnit: CeuUnit) => {
                if (ceuUnit.vesselId !== this.vessel.id) return;
                const found = this.applyToUnits(ceuUnit.fcuId, units => [...units, _.cloneDeep(ceuUnit)]);
                if (found) this.messageDialogService.openMessage('ceu-unit.created');
            });

        this.ceuSocketService
            .unitUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((ceuUnit: CeuUnit) => {
                if (ceuUnit.vesselId !== this.vessel.id) return;
                const found = this.applyToUnits(ceuUnit.fcuId, units =>
                    units.map(unit => (unit.id === ceuUnit.id ? _.cloneDeep(ceuUnit) : unit))
                );
                if (found) this.messageDialogService.openMessage('ceu-unit.updated', { index: ceuUnit.index });
            });

        this.ceuSocketService
            .unitDeleted$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ ceuUnits, deletedUnit }: { ceuUnits: CeuUnit[]; deletedUnit: CeuUnit }) => {
                if (deletedUnit.vesselId !== this.vessel.id) return;
                const newIndexById = new Map(ceuUnits.map(u => [u.id, u.index]));
                const found = this.applyToUnits(deletedUnit.fcuId, units =>
                    units
                        .filter(unit => unit.id !== deletedUnit.id)
                        .map(unit => ({ ...unit, index: newIndexById.get(unit.id) ?? unit.index }))
                );
                if (found) this.messageDialogService.openMessage('ceu-unit.deleted', { index: deletedUnit.index });
            });

        this.ceuSocketService
            .portsUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(async ({ vesselId }) => {
                if (vesselId !== this.vessel.id) return;
                const extComms = await this.externalCommunicationService.getListByVesselId(this.vessel.id);
                const ceuUnits = await this.ceuUnitService.getListByVesselId(this.vessel.id);
                this.communicationProtocols = await this.communicationProtocolService.getAll();

                this.commsList = this.prepareCommsList(extComms, ceuUnits);
                this.initialCommsList = _.cloneDeep(this.commsList);
                this.messageDialogService.openMessage('ext-comm-settings.updated');
            });

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroy$)).subscribe(canEdit => {
            this.userCanEdit = canEdit;
        });

        this.isLoading = true;
        try {
            const extComms = await this.externalCommunicationService.getListByVesselId(this.vessel.id);
            const ceuUnits = await this.ceuUnitService.getListByVesselId(this.vessel.id);
            this.communicationProtocols = await this.communicationProtocolService.getAll();

            this.commsList = this.prepareCommsList(extComms, ceuUnits);
            this.initialCommsList = _.cloneDeep(this.commsList);
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openProtocolList() {
        const url = this.router.serializeUrl(this.router.createUrlTree(['home/communication-protocols']));
        window.open(url, '_blank');
    }

    getFcuHeader(comm: FcuExternalCommunications) {
        return this.translateService.instant('dlg.FCU', { num: comm.fcuIndex });
    }

    getCEUHeader(ceuUnit: CeuUnit) {
        let ceuHeaderText = this.translateService.instant('dlg.CEU', { num: ceuUnit.index });
        if (ceuUnit.commType) ceuHeaderText = ceuHeaderText + ` (${ceuUnit.commType})`;
        return ceuHeaderText;
    }

    isAddDisabled(ceuUnits: CeuUnit[]): boolean {
        return ceuUnits.length >= ceuMaxNum;
    }

    async addUnit(fcuExtComms: FcuExternalCommunications) {
        const ceuUnits = fcuExtComms.ceuUnits;
        if (this.isAddDisabled(ceuUnits)) return;
        const newUnit: CeuUnit = {
            id: null,
            index: ceuUnits.length + 1,
            fcuId: fcuExtComms.fcuId,
            vesselId: this.vessel.id,
            Ports: [],
            commType: null,
            lineAIp: '',
            lineBIp: '',
        };
        const dialogRef = this.dialog.open(CeuUnitDialogComponent, {
            width: '472px',
            data: { ceuUnit: newUnit, isEditing: false },
        });

        const newCeuUnit = await lastValueFrom(dialogRef.afterClosed());
        if (newCeuUnit) {
            await this.ceuService.createUnit(newCeuUnit, fcuExtComms.fcuIndex);
        }
    }

    async handleEditCeuUnit(ceuUnit) {
        const dialogRef = this.dialog.open(CeuUnitDialogComponent, {
            width: '472px',
            data: { ceuUnit: ceuUnit, isEditing: true },
        });

        const newCeuUnit = await lastValueFrom(dialogRef.afterClosed());
        if (newCeuUnit) {
            const fcuUnit = this.commsList.find(fcu => fcu.fcuId == ceuUnit.fcuId);
            await this.ceuService.updateUnit(newCeuUnit, fcuUnit.fcuIndex);
        }
    }

    async handleDeleteCeuUnit(ceuUnit: CeuUnit) {
        const deleteTitle = this.translateService.instant('ceu-unit.delete.title', { index: ceuUnit.index });
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: deleteTitle,
                message: 'ceu-unit.delete.subLabel',
                discardButton: 'dlg.cancel',
                saveButton: 'dlg.delete',
                width: '452px',
            },
            disableClose: true,
        });
        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            const fcuUnit = this.commsList.find(fcu => fcu.fcuId == ceuUnit.fcuId);
            await this.ceuService.deleteByCeu(ceuUnit, fcuUnit.fcuIndex);
        }
    }

    cancelClick() {
        this.commsList = _.cloneDeep(this.initialCommsList);
    }

    async saveChanges() {
        try {
            this.isLoading = true;

            function diffById(before: any[], after: any[]) {
                const b = new Map(before.map(x => [x.id, x]));
                const a = new Map(after.map(x => [x.id, x]));
                return [
                    ...[...a.values()].filter(x => b.has(x.id) && !_.isEqual(b.get(x.id), x)),
                    ...[...a.values()].filter(x => !b.has(x.id)),
                ];
            }

            const extCommsDiffs = diffById(
                this.initialCommsList.map(f => f.extComms),
                this.commsList.map(f => f.extComms)
            );
            const ceuUnitsDiffs = diffById(
                this.initialCommsList.flatMap(f => f.ceuUnits),
                this.commsList.flatMap(f => f.ceuUnits)
            );

            if (extCommsDiffs) {
                await this.externalCommunicationService.updatePorts(extCommsDiffs, ceuUnitsDiffs.length > 0);
            }
            if (ceuUnitsDiffs) {
                await this.ceuUnitService.updatePorts(ceuUnitsDiffs);
            }
            this.initialCommsList = _.cloneDeep(this.commsList);
            this.messageDialogService.openMessage('ext-comm-settings.updated');
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    get hasDifferences() {
        return !_.isEqual(this.initialCommsList, this.commsList);
    }

    private prepareCommsList(extComms: ExternalCommunication[], ceuUnits: CeuUnit[]): FcuExternalCommunications[] {
        const comms: FcuExternalCommunications[] = [];

        if (extComms && extComms.length > 0) {
            extComms.forEach(extComm => {
                const fcuUnit = extComm.FcuUnit;
                const fcuId = fcuUnit.id;
                const fcuIndex = fcuUnit.index;
                const fcuComms = comms.find(comm => comm.fcuId == fcuId);
                if (fcuComms) fcuComms.extComms = extComm;
                else comms.push({ fcuId, fcuIndex, extComms: extComm, ceuUnits: [] });
            });
        }

        if (ceuUnits && ceuUnits.length > 0) {
            ceuUnits.forEach(ceuUnit => {
                const fcuUnit = ceuUnit.FcuUnit;
                const fcuId = fcuUnit.id;
                const fcuIndex = fcuUnit.index;
                const fcuComms = comms.find(comm => comm.fcuId == fcuId);
                if (fcuComms) fcuComms.ceuUnits.push(ceuUnit);
                else comms.push({ fcuId, fcuIndex, extComms: null, ceuUnits: [ceuUnit] });
            });
        }

        comms.sort((a, b) => a.fcuIndex - b.fcuIndex);
        comms.forEach(comm => comm.ceuUnits.sort((a, b) => a.index - b.index));

        return comms;
    }

    private applyToUnits(fcuId: string, transform: (units: CeuUnit[]) => CeuUnit[]): boolean {
        let foundInCommsList = false;

        for (const [list, isCommsList] of [
            [this.commsList, true],
            [this.initialCommsList, false],
        ] as const) {
            const fcu = list.find(f => f.fcuId === fcuId);
            if (!fcu) continue;
            fcu.ceuUnits = transform(fcu.ceuUnits);
            if (isCommsList) foundInCommsList = true;
        }

        return foundInCommsList;
    }
}
